import React, { useState, useCallback, useRef, useEffect } from 'react';
import './PromptConfig.css'; // reuse existing styles
import { 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Typography 
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useMsal } from '@azure/msal-react';
import { getPermittedDataSources } from '../Services/DataSourceService';
import { createBatchConfiguration, BatchConfigurationRequest, getDefaultTemplates } from '../Services/BatchConfigurationService';

type Company = string;

interface CompanyDataSource {
  id: number;
  company: string;
}

const MAX_WORDS = 8000;

const BatchPromptConfig: React.FC = () => {
  const { instance, accounts } = useMsal();
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [prompt1, setPrompt1] = useState('');
  const [prompt2, setPrompt2] = useState('');
  const [prompt3, setPrompt3] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Company>('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyDataSources, setCompanyDataSources] = useState<CompanyDataSource[]>([]);
  const [companyError, setCompanyError] = useState<string>('');
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [fileError, setFileError] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initRef = useRef(false);

  const countWords = useCallback((text: string) => text.trim().split(/\s+/).filter(w => w.length).length, []);

  // Data fetching function like in Chat.tsx
  const fetchCompanies = async () => {
    if (initRef.current || !accounts[0]) return;
    
    try {
      initRef.current = true;
      const response = await getPermittedDataSources(instance, accounts[0]);
      
      // Store full data source info with IDs
      setCompanyDataSources(response);
      
      // Extract unique company names for display
      const companies = Array.from(new Set(
        response.map((item: { company: string }) => item.company)
      )) as Company[];

      if (companies.length > 0) {
        setCompanies(companies);
        // Don't set a default company - let user select
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      initRef.current = false;
    }
  };

  // Fetch default templates
  const fetchDefaultTemplates = async () => {
    if (!accounts[0]) return;
    
    try {
      const templates = await getDefaultTemplates(instance, accounts[0]);
      setPrompt1(templates.prompt1);
      setPrompt2(templates.prompt2);
      setPrompt3(templates.prompt3);
    } catch (error) {
      console.error('Error fetching default templates:', error);
      // Continue with empty prompts if API call fails
    }
  };

  useEffect(() => {
    fetchCompanies();
    fetchDefaultTemplates();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const name = file.name.toLowerCase();
      const allowed = name.endsWith('.csv') || name.endsWith('.xls') || name.endsWith('.xlsx');
      if (!allowed) {
        setFileError('Only .csv, .xls, or .xlsx files are allowed');
        setSelectedFile(null);
        e.target.value = '';
        return;
      }
      setFileError('');
      setSelectedFile(file);
    }
  };

  const validateWordLimit = (value: string) => {
    const words = countWords(value);
    return words <= MAX_WORDS;
  };

  const handlePromptChange = (which: 1 | 2 | 3, value: string) => {
    const key = `prompt${which}`;
    
    // Clear required field error when user starts typing
    if (value.trim() && errors[key] === `Prompt ${which} is required`) {
      setErrors(prev => { const c = { ...prev }; delete c[key]; return c; });
    }
    
    // Check word limit
    if (!validateWordLimit(value)) {
      setErrors(prev => ({ ...prev, [key]: `Exceeds ${MAX_WORDS} word limit` }));
    } else if (errors[key] && errors[key].includes('word limit')) {
      // Only clear word limit errors, not required field errors
      setErrors(prev => { const c = { ...prev }; delete c[key]; return c; });
    }
    
    if (which === 1) setPrompt1(value); 
    else if (which === 2) setPrompt2(value); 
    else setPrompt3(value);
  };

  const handleStartBatch = async () => {
    // Validation first
    if (!selectedFile) {
      setFileError('Please upload a CSV or Excel file first');
      return;
    }
    
    // Debug logging
    console.log('Company validation:', {
      selectedCompany,
      selectedCompanyId,
      companyDataSources
    });
    
    if (!selectedCompany) {
      setCompanyError('Please select a company before starting the batch.');
      return;
    }
    
    // If company is selected but ID is missing, try to find it
    let companyId = selectedCompanyId;
    if (!companyId && selectedCompany) {
      const companyDataSource = companyDataSources.find(ds => ds.company === selectedCompany);
      if (companyDataSource) {
        companyId = companyDataSource.id;
        setSelectedCompanyId(companyId);
      } else {
        setCompanyError('Unable to find company data. Please reselect the company.');
        return;
      }
    }
    
    // Validate mandatory prompts
    if (!prompt1.trim()) {
      setErrors(prev => ({ ...prev, prompt1: 'Prompt 1 is required' }));
      return;
    }
    if (!prompt2.trim()) {
      setErrors(prev => ({ ...prev, prompt2: 'Prompt 2 is required' }));
      return;
    }
    
    setCompanyError(''); // Clear error if company is selected
    
    if (fileError) return;
    setSubmitting(true);
    setFileError('');
    
    // Final validation - ensure we have a valid company ID
    if (!companyId) {
      setCompanyError('Unable to find company data. Please reselect the company.');
      setSubmitting(false);
      return;
    }
    
    try {
      // Prepare the batch configuration request
      const batchConfigRequest: BatchConfigurationRequest = {
        file: selectedFile,
        companyDataSourcesId: companyId, // Now guaranteed to be a number
        prompt1: prompt1.trim(),
        prompt2: prompt2.trim(),
        prompt3: prompt3.trim(),
        status: 'queued' // Initial status
      };

      console.log('Starting batch with:', {
        file: selectedFile?.name,
        companyDataSourcesId: companyId,
        prompt1, prompt2, prompt3,
        company: selectedCompany,
        sizes: {
          prompt1Words: countWords(prompt1),
          prompt2Words: countWords(prompt2),
          prompt3Words: countWords(prompt3)
        }
      });

      // Call the service to create batch configuration
      const result = await createBatchConfiguration(instance, batchConfigRequest, accounts[0]);
      
      console.log('Batch configuration created:', result);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
      // Optionally reset form after successful submission
      // setSelectedFile(null);
      // setPrompt1('');
      // setPrompt2('');
      // setPrompt3('');
      // setSelectedCompany('');
      // setSelectedCompanyId(null);
      
    } catch (error) {
      console.error('Batch start failed:', error);
      setFileError('Failed to start batch configuration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const disabled = submitting || !!fileError; // no longer disable when file missing; we validate on click

  const renderPromptField = (label: string, value: string, which: 1 | 2 | 3) => {
    const key = `prompt${which}`;
    const wordCount = countWords(value);
    const isRequired = which === 1 || which === 2;
    const displayLabel = isRequired ? `${label} *` : label;
    
    return (
      <div className="prompt-section" style={{ marginBottom: 15 }}>
        <div style={{ display: 'flex', alignItems: 'left', gap: 10 }}>
          <span className="prompt-label" style={{ minWidth: 140 }}>{displayLabel}:</span>
          <div style={{ flexGrow: 1 }}>
            <textarea
              value={value}
              onChange={(e) => handlePromptChange(which, e.target.value)}
              placeholder={`Multiline text box with max ${MAX_WORDS} words allowed${isRequired ? ' (Required)' : ''}`}
              rows={3}
              className="prompt-textarea"
              style={{ width: '100%', resize: 'none' }}
            />
            <div className="prompt-word-count" style={{ textAlign: 'right', fontSize: 12, color: '#666' }}>
              {errors[key] ? <span style={{ color: 'red' }}>{errors[key]}</span> : `${wordCount}/${MAX_WORDS} words`}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="prompt-config-container prompt-config-outer">
      {submitting && (
        <div className="spinner-overlay">
          <div className="spinner spinner-large" />
        </div>
      )}
      {showSuccess && (
        <div className="success-popup">Batch started!</div>
      )}
      <div className="prompt-section" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <span className="prompt-label" style={{ minWidth: 200 }}>Questionnaire file:</span>
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <input
              ref={fileInputRef}
              hidden
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={handleFileChange}
              aria-label="Upload batch questionnaire (CSV or Excel)"
            />
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ 
                textTransform: 'none',
                fontSize: 16,
                padding: '8px 16px',
                minWidth: 'auto'
              }}
            >
              {selectedFile ? selectedFile.name : 'Upload Files'}
            </Button>
          </div>
        </div>
        {fileError && <div className="prompt-validation-error" style={{ marginTop: 8 }}>{fileError}</div>}
      </div>

      {renderPromptField('Prompt 1', prompt1, 1)}
      {renderPromptField('Prompt 2', prompt2, 2)}
      {renderPromptField('Prompt 3', prompt3, 3)}

      <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap', marginTop: 24, alignItems: 'flex-start' }}>
        <div style={{ flexGrow: 1 }}></div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24, paddingTop: '8px' }}>
          <FormControl className="prompt-form-control" error={!!companyError} style={{ minWidth: 200 }}>
            <InputLabel id="company-select-label">Company</InputLabel>
            <Select
              labelId="company-select-label"
              value={selectedCompany}
              label="Company"
              onChange={(e) => {
                const companyName = e.target.value as Company;
                console.log('Company selected:', companyName);
                console.log('Available company data sources:', companyDataSources);
                
                setSelectedCompany(companyName);
                
                // Find the corresponding company ID
                const companyDataSource = companyDataSources.find(ds => ds.company === companyName);
                console.log('Found company data source:', companyDataSource);
                
                if (companyDataSource) {
                  setSelectedCompanyId(companyDataSource.id);
                  console.log('Set company ID:', companyDataSource.id);
                } else {
                  console.warn('Company data source not found for:', companyName);
                  setSelectedCompanyId(null);
                }
                
                setCompanyError(''); // Clear error when company is selected
              }}
            >
              <MenuItem value="" disabled>
                <em>Select Company</em>
              </MenuItem>
              {companies.map((company) => (
                <MenuItem 
                  key={company} 
                  value={company}
                >
                  {company}
                </MenuItem>
              ))}
            </Select>
            {companyError && (
              <Typography 
                color="error" 
                variant="caption" 
                sx={{ 
                  mt: 0.5,
                  fontSize: '0.75rem',
                  lineHeight: '1.2'
                }}
              >
                {companyError}
              </Typography>
            )}
          </FormControl>
          <Button
            variant="contained"
            onClick={handleStartBatch}
            disabled={disabled}
            size="large"
            sx={{
              textTransform: 'none',
              fontSize: '18px',
              padding: '12px 32px',
              fontWeight: 500,
              backgroundColor: '#15343b',
              '&:hover': {
                backgroundColor: '#0f2a30'
              },
              '&:disabled': {
                backgroundColor: '#999'
              }
            }}
          >
            {submitting ? 'Starting...' : 'Start Batch'}
          </Button>
        </div>
      </div>      
    </div>
  );
};

export default BatchPromptConfig;
